import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
    CancelScanResponseSchema,
    GetCurrentScanResponseSchema,
    GetScanJobResponseSchema,
    ListScanHistoryRequestSchema,
    ListScanHistoryResponseSchema,
    PauseScanResponseSchema,
    ResetSampleScanResponseSchema,
    ResumeScanResponseSchema,
    RetryFailedRequestSchema,
    RetryFailedResponseSchema,
    StartScanRequestSchema,
    StartScanResponseSchema,
    WorkerHealthResponseSchema
} from '../schemas/scan';
import { defaultErrorResponses } from './errors';

const c = initContract()

/**
 * Scan Infrastructure Contract
 *
 * Handles single-active, user-initiated Gmail scan pipeline with:
 * - Streamed progress over Supabase realtime
 * - Graceful pause/resume with checkpoints
 * - Immediate email classification
 * - Delta scans for new mail
 * - Scan history exposure
 */
export const scanContract = c.router({
  /**
   * POST /v1/scan/start - Initiate a new scan
   *
   * Starts a new single-active scan job for the authenticated user.
   * Enforces one PENDING/IN_PROGRESS/PAUSED scan per user via query-first pattern.
   * Returns 409 if scan already in progress with existing job_id and status.
   */
  startScan: {
    method: 'POST',
    path: '/v1/scan/start',
    body: StartScanRequestSchema,
    responses: {
      200: StartScanResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Start a new Gmail scan',
      description: 'Initiates full or delta scan. Enforces single active scan per user.',
    },
  },

  /**
   * POST /v1/scan/reset-sample - Full reset + fresh sample scan
   *
   * Self-service: wipes the caller's own scan-derived data (emails,
   * suggestions, sender insights, exclusion hints, apply jobs, scan jobs),
   * cancels any active scan, then enqueues a brand-new sample scan. Uses the
   * caller's own authenticated session - only ever affects the caller's data.
   */
  resetSampleScan: {
    method: 'POST',
    path: '/v1/scan/reset-sample',
    body: z.object({}),
    responses: {
      200: ResetSampleScanResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Reset and re-run sample scan',
      description: 'Wipes the caller\'s scan data and enqueues a fresh sample scan.',
    },
  },

  /**
   * POST /v1/scan/:id/pause - Request scan pause
   *
   * Sets pause_requested_at flag. Worker finishes current batch, then pauses.
   * Returns 409 if job not in IN_PROGRESS or PAUSED status.
   */
  pauseScan: {
    method: 'POST',
    path: '/v1/scan/:id/pause',
    body: z.object({}),
    responses: {
      200: PauseScanResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Request scan pause',
      description: 'Gracefully pauses scan after finishing current batch.',
    },
  },

  /**
   * POST /v1/scan/:id/resume - Resume a paused scan
   *
   * Resumes from pause_requested or PAUSED state.
   * Validates resume_page_token; if invalid, emits checkpoint_invalid event and resets.
   * Returns 409 if job not in PAUSED or not pause_requested state.
   */
  resumeScan: {
    method: 'POST',
    path: '/v1/scan/:id/resume',
    body: z.object({}),
    responses: {
      200: ResumeScanResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Resume paused scan',
      description: 'Resumes scan from saved checkpoint.',
    },
  },

  /**
   * POST /v1/scan/:id/cancel - Cancel an active scan
   *
   * Cancels PENDING, IN_PROGRESS, or PAUSED scans.
   * Sets canceled_at timestamp and updates status to CANCELED.
   * Returns 409 if job already in terminal state (COMPLETED, FAILED, CANCELED).
   */
  cancelScan: {
    method: 'POST',
    path: '/v1/scan/:id/cancel',
    body: z.object({}),
    responses: {
      200: CancelScanResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Cancel active scan',
      description: 'Cancels scan and marks as CANCELED.',
    },
  },

  /**
   * GET /v1/scan/current - Get active scan for user
   *
   * Returns the currently active (non-terminal) scan job, or null if none.
   * Useful for UI to determine if scan is in progress.
   */
  getCurrentScan: {
    method: 'GET',
    path: '/v1/scan/current',
    responses: {
      200: GetCurrentScanResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get current active scan',
      description: 'Returns active scan job or null.',
    },
  },

  /**
   * GET /v1/scan/history - List all scans for user
   *
   * Returns paginated scan history with optional filtering.
   * Useful for debugging and showing user their scan history.
   */
  listScanHistory: {
    method: 'GET',
    path: '/v1/scan/history',
    query: ListScanHistoryRequestSchema,
    responses: {
      200: ListScanHistoryResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'List scan history',
      description: 'Returns paginated scan history for user.',
    },
  },

  /**
   * GET /v1/scan/:id - Get specific scan job
   *
   * Returns detailed state of a single scan job.
   * Includes progress counters, error details, and pause/resume state.
   */
  getScanJob: {
    method: 'GET',
    path: '/v1/scan/:id',
    responses: {
      200: GetScanJobResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get scan job details',
      description: 'Returns full state of specific scan job.',
    },
  },

  /**
   * POST /v1/scan/:id/retry-failed - Retry classification of failed messages
   *
   * Re-attempts classification for messages that failed during scan.
   * Optional message_ids subset; defaults to all failed messages.
   * Returns count of retried, succeeded, and still-failed messages.
   */
  retryFailed: {
    method: 'POST',
    path: '/v1/scan/:id/retry-failed',
    body: RetryFailedRequestSchema,
    responses: {
      200: RetryFailedResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Retry failed classifications',
      description: 'Re-attempts classification for messages that failed.',
    },
  },

  /**
   * GET /v1/scan/worker/health - Worker health check
   *
   * Health check for monitoring scan worker instances.
   * Useful for alerting when all workers are unhealthy.
   */
  getWorkerHealth: {
    method: 'GET',
    path: '/v1/scan/worker/health',
    responses: {
      200: WorkerHealthResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: false,
      summary: 'Worker health check',
      description: 'Returns health status of scan worker.',
    },
  },
})
